import React from "react";
import _ from "underscore";
import moment from "moment";
import classnames from "classnames";
import { HeadingWithIntro } from "./../../../core/atoms/atoms";
import { PaymentIframe } from "./../../views/customer/paymentIframe";
import { SharedActions } from "./../../../shared/flux/actions/sharedActions";
import { PriceInCurrency } from "../../../core/atoms/atoms";
import { Icon } from "armstrong-react";
import { Grid, Row, Col } from 'armstrong-react-fix';

import { add, subtract, isZero } from "../../../bothends/priceHelpers";
import { subtractDays, fromNow, formatDate, formatDateTime, isBefore, today } from "@travellocal/utils/dateHelpers";

import "./customerPaymentDetail.scss";

interface OwnProps {
  lto: ILtoSummary;
  customerEnquiry: IEnquiry;
  payments: IPayment[];
  paidSoFar: IPriceInCurrency;
  totalCost: IPriceInCurrency|null;
  departureDate: IDate|null;
  paidDeposit: boolean;
  showPricesForCustomer?: boolean;
  balanceDueDays: number;
}

export class CustomerPaymentDetail extends React.Component<OwnProps> {

  public render() {
    const { totalCost, paidSoFar, departureDate, customerEnquiry, showPricesForCustomer, payments, balanceDueDays } = this.props;

    const dueDate = departureDate ? subtractDays(departureDate, balanceDueDays) : null;

    const timeToPay = dueDate ? fromNow(dueDate) : null;
    const isLate = dueDate ? isBefore(dueDate, today()) : false;

    const paymentElements = payments.map(p => {
      if (p.isDigital) {
        return <DigitalPaymentItem showPricesForCustomer={showPricesForCustomer} key={p.id} payment={p as IDigitalPayment} />
      } else {
        return <NonDigitalPaymentItem showPricesForCustomer={showPricesForCustomer} key={p.id} payment={p as INonDigitalPayment} />
      }
    });

    return (
      <div>
        <Grid className="m-bottom-medium">
          {paymentElements.length === 0 &&
            <Row>
              <Col className="payment-detail-row no-payments">
                No payments have been made yet. Once they have, you'll be able to track and review them here.
            </Col>
            </Row>
          }
          {paymentElements}
        </Grid>

        <Grid className="m-bottom-large rs-medium-1col customer-payment-summary">
          <Row>
            <Col>
              <Grid>
                <Row className="p-bottom-xsmall">
                  <Col>Total paid to date</Col>
                  <Col width="auto">
                    <PriceInCurrency displayForCustomer={showPricesForCustomer} priceInCurrency={paidSoFar} />
                  </Col>
                </Row>
                {totalCost &&
                  <Row className="p-bottom-xsmall">
                    <Col>Total trip cost</Col>
                    <Col width="auto">
                      <strong><PriceInCurrency displayForCustomer={showPricesForCustomer} priceInCurrency={totalCost} /></strong>
                    </Col>
                  </Row>
                }
                <Row>
                  <Col className="p-bottom-xsmall">
                    <hr />
                  </Col>
                </Row>
                {totalCost &&
                  <Row>
                    <Col>Remaining balance</Col>
                    <Col width="auto">
                      <strong><PriceInCurrency displayForCustomer={showPricesForCustomer} priceInCurrency={subtract(totalCost, paidSoFar)} /></strong>
                    </Col>
                  </Row>
                }
                {(customerEnquiry.enquiryStatus !== EnquiryStatus.FullyPaid && !isLate && dueDate) &&
                  <Row>
                    <Col>
                      <p className="payment-detail-due-info">{`*Due on or before ${formatDate(dueDate, "DD/MM/YYYY")} (${timeToPay})`}</p>
                    </Col>
                  </Row>
                }
                {(customerEnquiry.enquiryStatus !== EnquiryStatus.FullyPaid && isLate) &&
                  <Row>
                    <Col>
                      <p className="payment-detail-due-info">{`*Due now`}</p>
                    </Col>
                  </Row>
                }
              </Grid>
            </Col>
            <Col width={20} />
          </Row>

        </Grid>
      </div>
    )
  }
}



interface IDigitalPaymentItemProps extends React.Props<DigitalPaymentItem> {
  payment: IDigitalPayment;
  showPricesForCustomer?: boolean;
}

export class DigitalPaymentItem extends React.Component<IDigitalPaymentItemProps, {}> {
  render() {
    let p = this.props.payment;
    let surcharge = p.charge != null
      ? subtract(p.charge, p.amount)
      : { price: 0, currency: p.amount.currency };
    let pending = p.paymentStatus && p.paymentStatus === PaymentStatus.Pending ? true : false;

    return (
      <Row className={classnames("payment-detail-row", {"payment-status-pending": pending})}>
        {pending &&
          <div className="payment-status-pending-message row m-bottom-small">
            <Icon className="payment-status-icon" icon={Icon.Icomoon.notification2} />Your payment is pending, and we have not yet received confirmation of a successful transaction from our payment provider. If you have any questions about this payment, please contact the TravelLocal team on +44 (0)117 325 7898. Please don't attempt to pay again until the status of this payment is confirmed.
          </div>
        }
        <Row>
          <Col>
            <h4>Digital card payment</h4>
            <ul>
              <li>{p.reference}</li>
              <li>{`Issued ${formatDateTime(p.time, "DD/MM/YY HH:mm:ss")}`}</li>
              {p.cardLastFourDigits && <li>Card # ends with {p.cardLastFourDigits}</li>}
            </ul>
          </Col>
          <Col width="auto" className="t-align-right">
            <ul>
              <li>Towards trip: <strong><PriceInCurrency displayForCustomer={this.props.showPricesForCustomer} priceInCurrency={p.amount} /></strong></li>
              {!isZero(surcharge) && <li>Card fee: <PriceInCurrency displayForCustomer={this.props.showPricesForCustomer} priceInCurrency={subtract(p.charge, p.amount)} /></li>}
              <li>Total charged: <PriceInCurrency displayForCustomer={this.props.showPricesForCustomer} priceInCurrency={p.charge} /></li>
            </ul>
          </Col>
        </Row>
      </Row>
    )
  }
}

interface INonDigitalPaymentItemProps extends React.Props<NonDigitalPaymentItem> {
  payment: INonDigitalPayment;
  showPricesForCustomer?: boolean;
}

export class NonDigitalPaymentItem extends React.Component<INonDigitalPaymentItemProps, {}> {
  render() {
    let p = this.props.payment;
    let charge;
    let surcharge;

    // If the charge has not been stored, no charge or surcharge
    if (!p.charge || p.charge.price == null) {
      charge = null
      surcharge = null
    } else {
      charge = p.charge
      surcharge = subtract(charge, p.amount);
    }

    let paymentTypeMessage = p.amount.price >= 0
      ? (p.cardLastFourDigits ? `Manual card payment` : `Manual payment`)
      : "Refund";

    return (
      <Row className="payment-detail-row">
        <Col>
          <h4>{paymentTypeMessage}</h4>
          <ul>
            <li>{p.reference}</li>
            <li>{`Issued ${formatDateTime(p.time, "DD/MM/YY HH:mm:ss")}`}</li>
            {p.cardLastFourDigits && <li>Card # ends with {p.cardLastFourDigits}</li>}
            {p.notes && <li>Notes: {p.notes}</li>}
          </ul>
        </Col>
        <Col width="auto" className="t-align-right">
          <ul>
            {p.amount.price >= 0 && <li>Towards trip: <strong><PriceInCurrency displayForCustomer={this.props.showPricesForCustomer} priceInCurrency={p.amount} /></strong></li>}
            {p.amount.price < 0 && <li><strong><PriceInCurrency displayForCustomer={this.props.showPricesForCustomer} priceInCurrency={p.amount} /></strong></li>}
            {surcharge != null && !isZero(surcharge) && <li>Card fee: <PriceInCurrency displayForCustomer={this.props.showPricesForCustomer} priceInCurrency={subtract(charge, p.amount)} /></li>}
            {charge != null && p.amount.price > 0 && <li>Total charged: <PriceInCurrency displayForCustomer={this.props.showPricesForCustomer} priceInCurrency={charge} /></li>}
          </ul>
        </Col>
      </Row>
    )
  }
}
